<template>
  <UserAvatarPopover
    v-if="assignedPerson.disponibility"
    :user-id="assignedPerson.id"
    :disponibility="assignedPerson?.disponibility?.assignement_status"
    :blink-icon="iconRole"
    :blink-icon-enabled="!!iconRole"
    related-model-type="App\Models\Event"
    :related-model-id="assignedPerson.disponibility.event_id"
    message-text-group="PLA"
    :second-icon="hasScheduleOffset ? 'timer' : null"
    second-icon-color="text-amber-500"
  >
    <template v-if="editable" #panel="{ close }">
      <div class="flex flex-col divide-y divide-slate-200">
        <div class="flex flex-col gap-2 pb-3">
          <div class="grid grid-cols-2 gap-2">
            <SelectComponent
              v-model="internalFromOffset"
              :values="fromOffsetOptions"
              :label="$t('attributes.arrival')"
              name="from_offset_minutes"
              edition-mode
              size="small"
              required
            />
            <SelectComponent
              v-model="internalToOffset"
              :values="toOffsetOptions"
              :label="$t('attributes.departure')"
              name="to_offset_minutes"
              edition-mode
              size="small"
              required
            />
          </div>
          <SelectAssignmentStatus
            v-model="internalAssignementStatus"
            edition-mode
            required
            size="small"
          />
          <SelectAssignedPersonFunctions
            v-model="internalFunction"
            edition-mode
            size="small"
          />
          <BaseButton
            v-show="needSave"
            class="w-full"
            :title="$t('button.save')"
            size="small"
            theme-color="bg-secondary text-contrast-secondary"
            @click="() => saveAssignedPerson(close)"
          />
        </div>
        <div class="pt-3">
          <BaseButton
            class="w-full"
            :title="$t('button.delete')"
            size="extra-small"
            color="danger"
            @click="() => removeUserFromEvent(close)"
          />
        </div>
      </div>
    </template>
  </UserAvatarPopover>
</template>

<script>
import SelectAssignmentStatus from '@c/InitialStateFormComponent/Enums/SelectAssignmentStatus.vue'
import UserAvatarPopover from '@c/Avatar/Popover/UserAvatarPopover.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { mapActions, mapGetters } from 'vuex'
import SelectAssignedPersonFunctions from '@c/InitialStateFormComponent/Enums/SelectAssignedPersonFunctions.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent'

const OFFSET_MINUTES = [0, 5, 10, 15, 20, 30, 45, 60]

export default {
  name: 'AssignedPerson',
  components: {
    SelectAssignedPersonFunctions,
    BaseButton,
    UserAvatarPopover,
    SelectAssignmentStatus,
    SelectComponent
  },
  props: {
    assignedPerson: {
      type: Object,
      required: true
    },
    eventId: {
      type: String,
      required: true
    },
    editable: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:assigned-person', 'event-updated', 'fetch-events'],
  data() {
    return {
      internalAssignementStatus:
        this.assignedPerson?.disponibility?.assignement_status,
      internalFunction: this.assignedPerson?.disponibility?.function,
      internalFromOffset:
        this.assignedPerson?.disponibility?.from_offset_minutes ?? 0,
      internalToOffset:
        this.assignedPerson?.disponibility?.to_offset_minutes ?? 0
    }
  },
  computed: {
    ...mapGetters({
      roles: 'metadata/getAssignedPersonFunctionsMetadata'
    }),
    iconRole() {
      return (
        this.roles.find(
          (role) => role.id === this.assignedPerson?.disponibility?.function
        )?.icon ?? null
      )
    },
    hasScheduleOffset() {
      return (
        !!this.assignedPerson?.disponibility?.from_offset_minutes ||
        !!this.assignedPerson?.disponibility?.to_offset_minutes
      )
    },
    localAssignedPerson: {
      get() {
        return this.assignedPerson
      },
      set(value) {
        this.$emit('update:assigned-person', value)
      }
    },
    fromOffsetOptions() {
      return OFFSET_MINUTES.map((minutes) => ({
        id: -minutes,
        name: minutes === 0 ? '0 min' : `- ${minutes} min`
      }))
    },
    toOffsetOptions() {
      return OFFSET_MINUTES.map((minutes) => ({
        id: minutes,
        name: minutes === 0 ? '0 min' : `+ ${minutes} min`
      }))
    },
    needSave() {
      return (
        this.localAssignedPerson.disponibility.assignement_status !==
          this.internalAssignementStatus ||
        this.localAssignedPerson.disponibility.function !==
          this.internalFunction ||
        (this.localAssignedPerson.disponibility.from_offset_minutes ?? 0) !==
          this.internalFromOffset ||
        (this.localAssignedPerson.disponibility.to_offset_minutes ?? 0) !==
          this.internalToOffset
      )
    }
  },
  methods: {
    ...mapActions({
      processUpdateAssignedPersonAction: 'planning/updateAssignedPerson',
      processRemoveAssignedPersonAction: 'planning/removeAssignedPerson'
    }),
    saveAssignedPerson(close) {
      this.processUpdateAssignedPersonAction({
        user_id: this.assignedPerson.disponibility.id,
        event_id: this.eventId,
        data: {
          function: this.internalFunction,
          assignement_status: this.internalAssignementStatus,
          from_offset_minutes: this.internalFromOffset || null,
          to_offset_minutes: this.internalToOffset || null
        }
      }).then((response) => {
        this.$h.toastSuccess(this.$t('toasts.updated'))
        this.$emit('event-updated', response.event)
        close()
      })
    },
    removeUserFromEvent(close) {
      this.processRemoveAssignedPersonAction({
        event_id: this.eventId,
        data: {
          assigned_users: [this.assignedPerson.disponibility.id]
        }
      })
        .then((response) => {
          this.$emit('event-updated', response.event)
          this.$emit('fetch-events')
          close()
        })
        .catch((_) => {
          this.$emit('fetch-events')
        })
    }
  }
}
</script>
